// Localização: frontend/src/utils/escalaExportUtils.js
// Utilitários para exportação de escalas de plantão em PDF e Excel

import jsPDF from 'jspdf';
import moment from 'moment';

/**
 * Formata data para exibição
 * @param {string} data - Data no formato YYYY-MM-DD
 * @returns {string} Data formatada
 */
const formatarData = (data) => {
    return moment(data).format('DD/MM/YYYY');
};

/**
 * Formata hora para exibição
 * @param {string} hora - Hora no formato HH:MM:SS
 * @returns {string} Hora formatada
 */
const formatarHora = (hora) => {
    if (!hora) return '';
    return hora.substring(0, 5); // HH:MM
};

/**
 * Formata tipo de plantão para exibição
 * @param {string} tipo - Tipo de plantão
 * @returns {string} Tipo formatado
 */
const formatarTipoPlantao = (tipo) => {
    const tipos = {
        'diurno': 'Diurno',
        'noturno': 'Noturno',
        'integral': 'Integral',
        'madrugada': 'Madrugada'
    };
    return tipos[tipo] || tipo;
};

/**
 * Gera PDF da escala de plantão
 * @param {Object} escala - Dados da escala
 * @param {Array} escala.itens - Itens da escala
 * @param {Object} escala.periodo - Período {inicio, fim}
 * @param {Object} escala.estatisticas - Estatísticas da escala
 * @param {string} organizacaoNome - Nome da organização
 * @returns {Promise<void>}
 */
export const exportarEscalaPDF = async (escala, organizacaoNome = '') => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    // Cores do Higeia
    const corHigeia = [16, 185, 129]; // emerald-500
    const corHigeiaLight = [209, 250, 229]; // emerald-100
    const corTexto = [17, 24, 39]; // gray-900
    const corTextoLight = [107, 114, 128]; // gray-500

    // Cabeçalho
    doc.setFillColor(...corHigeia);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Escala de Plantão', margin, 25);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (organizacaoNome) {
        doc.text(organizacaoNome, margin, 35);
    }
    
    yPos = 50;

    // Informações do período
    doc.setTextColor(...corTexto);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Período:', margin, yPos);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const periodoTexto = `${formatarData(escala.periodo?.inicio || escala.periodo_inicio)} a ${formatarData(escala.periodo?.fim || escala.periodo_fim)}`;
    doc.text(periodoTexto, margin + 30, yPos);
    
    yPos += 10;

    // Estatísticas (se disponíveis)
    if (escala.estatisticas) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('Estatísticas:', margin, yPos);
        yPos += 7;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        
        if (escala.estatisticas.distribuicao_equitativa_score !== undefined) {
            const score = (escala.estatisticas.distribuicao_equitativa_score * 100).toFixed(0);
            doc.text(`• Distribuição Equitativa: ${score}%`, margin + 5, yPos);
            yPos += 5;
        }
        
        if (escala.estatisticas.preferencias_atendidas_score !== undefined) {
            const score = (escala.estatisticas.preferencias_atendidas_score * 100).toFixed(0);
            doc.text(`• Preferências Atendidas: ${score}%`, margin + 5, yPos);
            yPos += 5;
        }
        
        if (escala.estatisticas.conflitos_detectados !== undefined) {
            doc.text(`• Conflitos Detectados: ${escala.estatisticas.conflitos_detectados}`, margin + 5, yPos);
            yPos += 5;
        }
        
        yPos += 5;
    }

    // Tabela de itens
    const itens = escala.itens || escala.sugestao?.itens || [];
    
    if (itens.length === 0) {
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...corTextoLight);
        doc.text('Nenhum plantão registrado nesta escala.', margin, yPos);
        doc.save(`escala-plantao-${moment().format('YYYY-MM-DD')}.pdf`);
        return;
    }

    // Ordenar itens por data e hora
    const itensOrdenados = [...itens].sort((a, b) => {
        const dataA = moment(`${a.data_plantao} ${a.hora_inicio}`);
        const dataB = moment(`${b.data_plantao} ${b.hora_inicio}`);
        return dataA.diff(dataB);
    });

    // Cabeçalho da tabela
    doc.setFillColor(...corHigeiaLight);
    doc.rect(margin, yPos - 5, pageWidth - (margin * 2), 8, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...corTexto);
    
    const colWidths = {
        data: 30,
        profissional: 50,
        horario: 35,
        tipo: 25,
        paciente: 40
    };
    
    let xPos = margin + 5;
    doc.text('Data', xPos, yPos);
    xPos += colWidths.data;
    doc.text('Profissional', xPos, yPos);
    xPos += colWidths.profissional;
    doc.text('Horário', xPos, yPos);
    xPos += colWidths.horario;
    doc.text('Tipo', xPos, yPos);
    xPos += colWidths.tipo;
    doc.text('Paciente', xPos, yPos);
    
    yPos += 8;

    // Linhas da tabela
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    
    itensOrdenados.forEach((item, index) => {
        // Verificar se precisa de nova página
        if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = margin;
            
            // Repetir cabeçalho
            doc.setFillColor(...corHigeiaLight);
            doc.rect(margin, yPos - 5, pageWidth - (margin * 2), 8, 'F');
            
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            xPos = margin + 5;
            doc.text('Data', xPos, yPos);
            xPos += colWidths.data;
            doc.text('Profissional', xPos, yPos);
            xPos += colWidths.profissional;
            doc.text('Horário', xPos, yPos);
            xPos += colWidths.horario;
            doc.text('Tipo', xPos, yPos);
            xPos += colWidths.tipo;
            doc.text('Paciente', xPos, yPos);
            
            yPos += 8;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
        }

        // Alternar cor de fundo
        if (index % 2 === 0) {
            doc.setFillColor(249, 250, 251); // gray-50
            doc.rect(margin, yPos - 4, pageWidth - (margin * 2), 6, 'F');
        }

        doc.setTextColor(...corTexto);
        xPos = margin + 5;
        
        // Data
        doc.text(formatarData(item.data_plantao), xPos, yPos);
        xPos += colWidths.data;
        
        // Profissional
        const nomeProfissional = item.usuario_nome || 'N/A';
        doc.text(nomeProfissional.length > 20 ? nomeProfissional.substring(0, 20) + '...' : nomeProfissional, xPos, yPos);
        xPos += colWidths.profissional;
        
        // Horário
        const horario = `${formatarHora(item.hora_inicio)} - ${formatarHora(item.hora_fim)}`;
        doc.text(horario, xPos, yPos);
        xPos += colWidths.horario;
        
        // Tipo
        doc.text(formatarTipoPlantao(item.tipo_plantao), xPos, yPos);
        xPos += colWidths.tipo;
        
        // Paciente
        const nomePaciente = item.paciente_nome || 'N/A';
        doc.text(nomePaciente.length > 18 ? nomePaciente.substring(0, 18) + '...' : nomePaciente, xPos, yPos);
        
        yPos += 6;
    });

    // Rodapé
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(...corTextoLight);
        doc.text(
            `Gerado em ${moment().format('DD/MM/YYYY HH:mm')} - Página ${i} de ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
        );
    }

    // Salvar PDF
    const nomeArquivo = `escala-plantao-${moment(escala.periodo?.inicio || escala.periodo_inicio).format('YYYY-MM-DD')}.pdf`;
    doc.save(nomeArquivo);
};

/**
 * Gera CSV/Excel da escala de plantão
 * @param {Object} escala - Dados da escala
 * @param {Array} escala.itens - Itens da escala
 * @param {Object} escala.periodo - Período {inicio, fim}
 * @returns {Promise<void>}
 */
export const exportarEscalaCSV = async (escala) => {
    const itens = escala.itens || escala.sugestao?.itens || [];
    
    // Ordenar itens por data e hora
    const itensOrdenados = [...itens].sort((a, b) => {
        const dataA = moment(`${a.data_plantao} ${a.hora_inicio}`);
        const dataB = moment(`${b.data_plantao} ${b.hora_inicio}`);
        return dataA.diff(dataB);
    });

    // Cabeçalho CSV
    const headers = ['Data', 'Profissional', 'Horário Início', 'Horário Fim', 'Tipo Plantão', 'Paciente', 'Justificativa'];
    
    // Linhas de dados
    const linhas = itensOrdenados.map(item => [
        formatarData(item.data_plantao),
        item.usuario_nome || 'N/A',
        formatarHora(item.hora_inicio),
        formatarHora(item.hora_fim),
        formatarTipoPlantao(item.tipo_plantao),
        item.paciente_nome || 'N/A',
        (item.justificativa || '').replace(/"/g, '""') // Escapar aspas duplas
    ]);

    // Criar conteúdo CSV
    const csvContent = [
        headers.join(','),
        ...linhas.map(linha => linha.map(campo => `"${campo}"`).join(','))
    ].join('\n');

    // Adicionar BOM para Excel reconhecer UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Criar link de download
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `escala-plantao-${moment(escala.periodo?.inicio || escala.periodo_inicio).format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Gera exportação da escala no formato especificado
 * @param {Object} escala - Dados da escala
 * @param {string} formato - Formato de exportação ('pdf' ou 'csv')
 * @param {string} organizacaoNome - Nome da organização (opcional)
 * @returns {Promise<void>}
 */
export const exportarEscala = async (escala, formato = 'pdf', organizacaoNome = '') => {
    try {
        if (formato === 'pdf') {
            await exportarEscalaPDF(escala, organizacaoNome);
        } else if (formato === 'csv' || formato === 'excel') {
            await exportarEscalaCSV(escala);
        } else {
            throw new Error(`Formato não suportado: ${formato}`);
        }
    } catch (error) {
        console.error('Erro ao exportar escala:', error);
        throw error;
    }
};

export default {
    exportarEscala,
    exportarEscalaPDF,
    exportarEscalaCSV
};

